/**
 * M7 S39: the Finale screen's model ("Transit Priority", spec §8.1–§8.4). One flow per commit, the first one and
 * every ladder move alike:
 *
 * 1. **Offer:** the four paths; a path not on offer shows its typed refusal and can't be picked.
 * 2. **Ask:** the asker's comms node (the §3.1 primary contact or its fallback, said in one line). An accept reply
 *    goes on; any other reply goes back. Voss's "You sold it." plays her answer first (real data only, §17).
 * 3. **Roll call:** `rollCall(career, to)` node by node; each reply goes to the next.
 * 4. **Hand-in:** Expose and Sell open S34's bundle picker (the host mounts it and reports `handedIn`).
 * 5. **Confirm:** the move's cost lines, where it is staged (PC-7: the Candle while lit), and that the act is final.
 * 6. **Done:** the Wire epilogue preview, each line with its outlet.
 *
 * The source (`story-link/finale.ts FinaleSource`) does the reads and the commit; the model only moves between
 * steps. Keyboard and pad: up and down move, activate presses, back steps back (and closes from the offer).
 */
import type { CommsView } from '../../../story/comms'
import { commsConfig } from '../../../story/comms'
import type { FinaleAsker, FinaleSource } from '../../../story-link/finale'
import type { FinalePath } from '../../../story/types'
import type { StoryWireOutlet } from '../../../strategic/types'
import { WIRE_OUTLET_LABELS } from '../../strategic/wireModel'
import type { NavInput } from '../../strategic/mount'
import finaleScreenJson from '../finaleScreen.json'

export type FinaleScreenConfig = typeof finaleScreenJson
export const FINALE_SCREEN: FinaleScreenConfig = finaleScreenJson

export type FinaleStep =
  | { kind: 'offer' }
  | { kind: 'ask'; path: FinalePath; nodes: readonly string[]; index: number }
  | { kind: 'roll'; path: FinalePath; nodes: readonly string[]; index: number }
  | { kind: 'bundle'; path: FinalePath }
  | { kind: 'confirm'; path: FinalePath }
  | { kind: 'done'; path: FinalePath }

export interface FinaleState {
  step: FinaleStep
  row: number
  notice: string | null
}

export type FinaleAction = { type: 'nav'; input: NavInput } | { type: 'press'; index: number } | { type: 'handedIn'; ok: boolean }

export type FinaleEffect = { kind: 'close' } | null

export interface FinaleButton {
  id: string
  label: string
  disabled: boolean
  /** A refusal or the asker's line under an offer row. */
  note: string | null
}

export interface FinaleView {
  title: string
  step: FinaleStep['kind']
  intro: string | null
  /** The node on screen (ask and roll call). */
  scene: CommsView | null
  /** Lines under the heading (the confirm's costs and staging). */
  lines: readonly string[]
  heading: string | null
  /** The epilogue preview (done). */
  wire: readonly { headline: string; outlet: string }[]
  buttons: readonly FinaleButton[]
}

export function createFinaleState(): FinaleState {
  return { step: { kind: 'offer' }, row: 0, notice: null }
}

function fill(text: string, name: string): string {
  return text.replace('{name}', name)
}

/** The asker's one line: how they reach the player, and that the usual door is shut when it is. */
export function askerLine(asker: FinaleAsker, cfg: FinaleScreenConfig = FINALE_SCREEN): string {
  const name = commsConfig().speakers[asker.contact.option.speaker].name
  const how = fill(cfg.channels[asker.contact.option.channel], name)
  return asker.contact.primary ? how : `${fill(cfg.fallback, name)} ${how}`
}

function outletLabel(o: StoryWireOutlet | null, cfg: FinaleScreenConfig): string {
  return o === null ? cfg.noOutlet : WIRE_OUTLET_LABELS[o]
}

export function buildFinaleView(s: FinaleState, src: FinaleSource, cfg: FinaleScreenConfig = FINALE_SCREEN): FinaleView {
  const base: FinaleView = { title: cfg.title, step: s.step.kind, intro: null, scene: null, lines: [], heading: null, wire: [], buttons: [] }
  const step = s.step
  switch (step.kind) {
    case 'offer': {
      const buttons: FinaleButton[] = src.offers().map((o) => {
        const asker = o.refusal === null ? src.ask(o.path, false) : null
        const note = o.refusal !== null ? cfg.refusals[o.refusal] : asker === null ? null : askerLine(asker, cfg)
        return { id: o.path, label: cfg.paths[o.path], disabled: o.refusal !== null, note }
      })
      buttons.push({ id: 'close', label: cfg.close, disabled: false, note: null })
      return { ...base, intro: cfg.offerIntro, buttons }
    }
    case 'ask':
    case 'roll': {
      const scene = src.node(step.nodes[step.index] ?? '')
      const replies = scene?.replies ?? []
      const buttons: FinaleButton[] =
        replies.length > 0 ? replies.map((r) => ({ id: r.id, label: r.text, disabled: false, note: null })) : [{ id: 'ok', label: cfg.decline, disabled: false, note: null }]
      return { ...base, heading: step.kind === 'roll' ? cfg.rollHeading : null, scene, buttons }
    }
    case 'bundle':
      return base
    case 'confirm': {
      const key = src.moveKey(step.path)
      const costs: Record<string, readonly string[] | undefined> = cfg.costs
      const lines = [...(costs[key] ?? []), cfg.staging[src.staging(step.path)], cfg.permanent]
      return {
        ...base,
        heading: cfg.confirmHeading,
        lines,
        buttons: [
          { id: 'commit', label: cfg.confirm, disabled: false, note: null },
          { id: 'back', label: cfg.back, disabled: false, note: null },
        ],
      }
    }
    case 'done':
      return {
        ...base,
        heading: cfg.doneHeading,
        wire: src.epilogue().map((l) => ({ headline: l.headline, outlet: outletLabel(l.outlet, cfg) })),
        buttons: [{ id: 'close', label: cfg.close, disabled: false, note: null }],
      }
  }
}

function at(step: FinaleStep): FinaleState {
  return { step, row: 0, notice: null }
}

/** After the roll call: the hand-in for Expose and Sell, else the confirmation. */
function afterRoll(path: FinalePath): FinaleState {
  return at(path === 'expose' || path === 'sell' ? { kind: 'bundle', path } : { kind: 'confirm', path })
}

function startRoll(path: FinalePath, src: FinaleSource): FinaleState {
  const nodes = src.rollCall(path)
  return nodes.length === 0 ? afterRoll(path) : at({ kind: 'roll', path, nodes, index: 0 })
}

function press(s: FinaleState, src: FinaleSource, view: FinaleView, index: number, cfg: FinaleScreenConfig): { state: FinaleState; effect: FinaleEffect } {
  const b = view.buttons[index]
  if (b === undefined) return { state: s, effect: null }
  if (b.disabled) return { state: { ...s, row: index, notice: b.note }, effect: null }
  const step = s.step
  switch (step.kind) {
    case 'offer': {
      if (b.id === 'close') return { state: s, effect: { kind: 'close' } }
      const path = b.id as FinalePath
      const asker = src.ask(path, true)
      if (asker === null) return { state: s, effect: null }
      return { state: at({ kind: 'ask', path, nodes: [asker.nodeId], index: 0 }), effect: null }
    }
    case 'ask': {
      const last = step.index >= step.nodes.length - 1
      if (step.index === 0) {
        const asker = src.ask(step.path, false)
        if (asker === null || !asker.accept.includes(b.id)) return { state: createFinaleState(), effect: null }
        const answer = src.bothSold(b.id)
        if (answer !== null) return { state: at({ ...step, nodes: [...step.nodes, answer], index: 1 }), effect: null }
        return { state: startRoll(step.path, src), effect: null }
      }
      return { state: last ? startRoll(step.path, src) : at({ ...step, index: step.index + 1 }), effect: null }
    }
    case 'roll':
      return { state: step.index >= step.nodes.length - 1 ? afterRoll(step.path) : at({ ...step, index: step.index + 1 }), effect: null }
    case 'bundle':
      return { state: s, effect: null }
    case 'confirm': {
      if (b.id === 'back') return { state: createFinaleState(), effect: null }
      const r = src.commit(step.path)
      if (r.ok) return { state: at({ kind: 'done', path: step.path }), effect: null }
      const reason = r.reason === 'noCareer' ? null : cfg.refusals[r.reason]
      return { state: { ...createFinaleState(), notice: reason }, effect: null }
    }
    case 'done':
      return { state: s, effect: { kind: 'close' } }
  }
}

export function reduceFinale(
  s: FinaleState,
  a: FinaleAction,
  src: FinaleSource,
  view: FinaleView,
  cfg: FinaleScreenConfig = FINALE_SCREEN,
): { state: FinaleState; effect: FinaleEffect } {
  if (a.type === 'handedIn') {
    if (s.step.kind !== 'bundle') return { state: s, effect: null }
    return { state: a.ok ? at({ kind: 'confirm', path: s.step.path }) : createFinaleState(), effect: null }
  }
  if (a.type === 'press') return press(s, src, view, a.index, cfg)
  const n = view.buttons.length
  switch (a.input) {
    case 'up':
      return { state: { ...s, row: Math.max(0, s.row - 1) }, effect: null }
    case 'down':
      return { state: { ...s, row: Math.min(Math.max(0, n - 1), s.row + 1) }, effect: null }
    case 'activate':
      return press(s, src, view, s.row, cfg)
    case 'back':
    case 'start':
      if (s.step.kind === 'offer' || s.step.kind === 'done') return { state: s, effect: { kind: 'close' } }
      return { state: createFinaleState(), effect: null }
    case 'left':
    case 'right':
      return { state: s, effect: null }
  }
}
